import {inject, Injectable} from "@angular/core";
import {Observable} from "rxjs";
import {map, shareReplay} from "rxjs/operators";
import {LegalRepo} from "../repositories/api/legal.repo";
import {LegalDocumentDto} from "../repositories/dto/legal-document.dto";
import {AgreementDto} from "../repositories/dto/agreement.dto";
import {UserRepo} from "../repositories/api/user.repo";
import {notDefined} from "../libs/utils/epic.utils";

/**
 * Rechtstexte. Sie kommen ausschliesslich von `core`, das sie aus `/legal`
 * ausliefert — `dash` haelt keinen eigenen Bestand und rendert nichts selbst.
 *
 * Grund: Der mit einer Zustimmung gespeicherte Hash muss ueber genau den
 * Wortlaut gebildet sein, der auf dem Bildschirm stand. Eine Stelle liefert
 * aus, dieselbe Stelle hasht. Siehe `legal/README.md`.
 */
@Injectable({providedIn: 'root'})
export class LegalService {

  /** Impressum nach § 5 DDG. */
  public static readonly LEGAL_NOTICE: string = 'legal-notice';

  public static readonly TERMS_OF_USE: string = 'terms-of-use';
  public static readonly PRIVACY_POLICY: string = 'privacy-policy';

  /** Auftragsverarbeitungsvertrag samt Anlage 3 und 4. */
  public static readonly DPA: string = 'dpa';

  /** Anlage 1 des AVV - eigene Fassung, weil Paragraph 6 Abs. 2 sie einseitig aenderbar macht. */
  public static readonly SECURITY_MEASURES: string = 'security-measures';

  /** Anlage 2 des AVV - eigene Fassung, weil Paragraph 7 sie einseitig aenderbar macht. */
  public static readonly SUBPROCESSORS: string = 'subprocessors';

  /** Standardvertragsklauseln (EU) 2021/914, Modul 2 - werden gemeinsam mit dem AVV abgeschlossen. */
  public static readonly SCC: string = 'scc';

  private readonly legalRepo: LegalRepo = inject(LegalRepo);
  private readonly userRepo: UserRepo = inject(UserRepo);

  private readonly inForce: Map<string, Observable<LegalDocumentDto>> = new Map<string, Observable<LegalDocumentDto>>();

  /** Die geltende Fassung. Pro Sitzung einmal geholt — der Text aendert sich waehrend eines Besuchs nicht. */
  public getInForce(document: string): Observable<LegalDocumentDto> {
    let request: Observable<LegalDocumentDto> | undefined = this.inForce.get(document);
    if (notDefined(request)) {
      request = this.legalRepo.getInForce(document).pipe(
        map((w) => w.getFirstData()),
        shareReplay({bufferSize: 1, refCount: false})
      );
      this.inForce.set(document, request);
    }
    return request;
  }

  /**
   * Die erteilten Zustimmungen des angemeldeten Nutzers.
   *
   * Nicht zwischengespeichert: Sie aendern sich nur bei einem Vertragsabschluss, und der ist selten
   * genug, dass ein veralteter Stand mehr kostet als der zusaetzliche Abruf.
   */
  public getAgreements(): Observable<AgreementDto[]> {
    return this.userRepo.getAgreements().pipe(map((w) => w.getDatas()));
  }

  /** Eine bestimmte Fassung — damit ein Kunde nachlesen kann, was er seinerzeit angenommen hat. */
  public getByVersion(document: string, version: number): Observable<LegalDocumentDto> {
    return this.legalRepo.getByVersion(document, version).pipe(map((w) => w.getFirstData()));
  }
}
